import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  confirmInput?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmText,
  cancelText,
  danger = false,
  confirmInput,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");

  const resolvedConfirmText = confirmText ?? t('dashboard.confirm.confirm');
  const resolvedCancelText = cancelText ?? t('dashboard.confirm.cancel');

  useEffect(() => {
    if (!open) setInputValue("");
  }, [open]);

  if (!open) return null;

  const canConfirm = !confirmInput || inputValue === confirmInput;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-[#f5f5f5] mb-2">{title}</h3>
        <p className="text-[#999] text-sm mb-4">{message}</p>

        {confirmInput && (
          <div className="mb-4">
            <label className="block text-[#999] text-xs mb-1">
              <Trans
                i18nKey="dashboard.confirm.typeToConfirm"
                values={{ value: confirmInput }}
                components={{ mono: <span className="text-[#f5f5f5] font-mono" /> }}
              />
            </label>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              autoFocus
            />
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-[#999] hover:text-[#f5f5f5] transition-colors"
          >
            {resolvedCancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed
              ${
                danger
                  ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                  : "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
              }`}
          >
            {resolvedConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
